import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/shared";
import { useSessionDwell } from "@/hooks/useSessionDwell";

const PALETTE = {
  ink: "#1a1a1a",
  ink2: "#3a3530",
  muted: "#5a5249",
  amber: "#C4823A",
  amberSoft: "#E8B458",
  card: "#FFFFFF",
  line: "rgba(0,0,0,0.08)",
};
const FONT_BODY = "Barlow, -apple-system, sans-serif";
const FONT_DISPLAY = "'Instrument Serif', Georgia, serif";

const STORAGE_KEY = "eva_exit_intent_seen_v1";
const SESSION_KEY = "eva_exit_intent_session_v1";
const ROI_TOUCHED_KEY = "roi_calc_touched";
const COOLDOWN_DAYS = 7;

const wasSeenRecently = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return (Date.now() - ts) < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
};
const wasSeenThisSession = () => {
  try { return sessionStorage.getItem(SESSION_KEY) === "1"; } catch { return false; }
};

export const ExitIntent = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const armed = useRef(true);
  // P22.5 — don't pop the modal until the user has spent 5 min on site.
  const dwellOk = useSessionDwell(5);

  // Only on home route
  useEffect(() => {
    if (pathname !== "/" && pathname !== "/services") return;
    if (!dwellOk) return;
    if (wasSeenRecently()) return;
    if (wasSeenThisSession()) return;

    const fineMq = window.matchMedia("(pointer: fine)");

    // Desktop: mouseleave to top
    const onLeave = (e: MouseEvent) => {
      if (!armed.current) return;
      if (e.clientY > 0) return;
      armed.current = false;
      setOpen(true);
    };

    // Mobile (P15.1): NEVER fire on scroll. Only trigger on tab-leave / page-hide,
    // AND ONLY if the user actually engaged with the ROI calculator (real intent signal).
    const isEngaged = () => {
      try { return localStorage.getItem(ROI_TOUCHED_KEY) === "1"; } catch { return false; }
    };
    const onHide = () => {
      if (!armed.current) return;
      if (document.visibilityState !== "hidden") return;
      if (!isEngaged()) return;
      armed.current = false;
      setOpen(true);
    };
    const onPageHide = () => {
      if (!armed.current) return;
      if (!isEngaged()) return;
      armed.current = false;
      setOpen(true);
    };

    if (fineMq.matches) {
      document.addEventListener("mouseleave", onLeave);
    } else {
      document.addEventListener("visibilitychange", onHide);
      window.addEventListener("pagehide", onPageHide);
    }
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [pathname, dwellOk]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error"); return;
    }
    setStatus("sending");
    try {
      let snapshot: unknown = {};
      try {
        const raw = localStorage.getItem("roi_snapshot");
        if (raw) snapshot = JSON.parse(raw);
      } catch {}
      const { error } = await supabase.functions.invoke("send-roi-capture-email", {
        body: { email, snapshot },
      });
      if (error) throw error;
      setStatus("done");
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
      window.setTimeout(() => setOpen(false), 1800);
    } catch {
      setStatus("error");
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(15,12,8,0.4)",
        backdropFilter: "blur(4px)",
        display: "grid", placeItems: "center",
        padding: 20,
        animation: "ei-fade 220ms cubic-bezier(.2,.8,.2,1) both",
      }}
    >
      <div style={{
        background: PALETTE.card, borderRadius: 22,
        maxWidth: 440, width: "100%",
        padding: "32px 28px 28px",
        boxShadow: "0 40px 80px -20px rgba(0,0,0,0.45)",
        position: "relative",
        animation: "ei-pop 280ms cubic-bezier(.2,.9,.3,1.1) both",
        fontFamily: FONT_BODY,
      }}>
        <button
          onClick={close}
          aria-label="Close"
          style={{
            position: "absolute", top: 14, right: 14,
            width: 32, height: 32, borderRadius: "50%",
            background: "transparent", border: 0, cursor: "pointer",
            color: PALETTE.muted, fontSize: 20, lineHeight: 1,
            display: "grid", placeItems: "center",
          }}
        >×</button>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 999, background: "rgba(196,130,58,0.12)", color: PALETTE.amber, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 16 }}>
          <span aria-hidden>✦</span> Wait —
        </div>
        <h2 id="exit-intent-title" style={{
          fontFamily: FONT_DISPLAY, fontStyle: "italic", fontWeight: 400,
          fontSize: 32, lineHeight: 1.1, margin: "0 0 12px", color: PALETTE.ink,
        }}>
          Save your ROI number?
        </h2>
        <p style={{ margin: "0 0 22px", fontSize: 14.5, color: PALETTE.ink2, lineHeight: 1.55 }}>
          We'll email you a 1-pager with your projected savings, the setup checklist, and what week one looks like. No spam, no follow-up if you don't reply.
        </p>
        {status === "done" ? (
          <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(196,130,58,0.1)", border: `1px solid rgba(196,130,58,0.3)`, color: PALETTE.ink, fontSize: 14, textAlign: "center" }}>
            ✓ On its way. Check your inbox in a few minutes.
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              type="email"
              required
              autoFocus
              placeholder="you@yourbusiness.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
              style={{
                padding: "13px 16px", borderRadius: 12,
                border: `1px solid ${status === "error" ? "#c44" : PALETTE.line}`,
                background: "#fff", fontSize: 15, color: PALETTE.ink,
                outline: "none", fontFamily: "inherit",
              }}
            />
            <button
              type="submit"
              disabled={status === "sending"}
              style={{
                padding: "13px 18px", borderRadius: 999, border: 0,
                background: PALETTE.ink, color: "#fff",
                fontSize: 14, fontWeight: 600, cursor: "pointer",
                opacity: status === "sending" ? 0.6 : 1,
              }}
            >
              {status === "sending" ? "Sending…" : "Email me the savings →"}
            </button>
            {status === "error" && (
              <div style={{ fontSize: 12, color: "#c44" }}>Couldn't send. Check the email and try again.</div>
            )}
            <button
              type="button"
              onClick={close}
              style={{ marginTop: 4, padding: 6, background: "transparent", border: 0, color: PALETTE.muted, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}
            >
              No thanks
            </button>
          </form>
        )}
      </div>
      <style>{`
        @keyframes ei-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes ei-pop  { from { opacity: 0; transform: translateY(12px) scale(0.96) } to { opacity: 1; transform: none } }
        @media (prefers-reduced-motion: reduce) {
          [role="dialog"] > div { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default ExitIntent;
